import { AutoComplete } from "frontend/src/components/AutoComplete/AutoComplete";
import { useQuery } from "@apollo/client";
import * as types from "_graphql-types/graphql";

import i18n from "i18next";
import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  removeFilter,
  updateFilter,
} from "Reducers/globalSearchV2/globalSearchV2.actions";
import { getInvestmentFilters } from "Reducers/globalSearchV2/globalSearchV2.selectors";
import { useDispatch } from "src/react-redux";
import { FilterInterface } from "../FilterInterface";
import { ADDRESS_SEARCH } from "../graphql";

function onlyUnique(location: any, index: number, self: any[]) {
  return (
    index ===
    self.findIndex(
      t =>
        t.country === location.country &&
        t.countryEnumId === location.countryEnumId &&
        t.state === location.state &&
        t.stateEnumId === location.stateEnumId
    )
  );
}

const locationText = (location: any) =>
  [location.city, location.state, location.country]
    .filter((v: string | null) => v)
    .join(", ");

const options = (locations?: types.AddressSearchQuery["addressSearch"]) => {
  if (!locations || locations.length === 0) {
    return [];
  }

  const countryOnly = locations
    .map(location => ({
      country: location.country,
      countryEnumId: location.countryEnumId,
      state: null,
      stateEnumId: null,
      city: null,
    }))
    .filter(onlyUnique);

  const countryState = locations
    .map(location => ({
      country: location.country,
      countryEnumId: location.countryEnumId,
      state: location.state,
      stateEnumId: location.stateEnumId,
      city: null,
    }))
    .filter(location => location.state)
    .filter(onlyUnique);

  return [...locations, ...countryOnly, ...countryState].filter(onlyUnique);
};

function HeadQuartersFilter() {
  const investmentFilters = useSelector(getInvestmentFilters);

  const dispatch = useDispatch();

  const headquartersFilter: types.LocationFilter | undefined = useMemo(() => {
    for (const filter of investmentFilters) {
      if (filter.type === "firm" && filter.data.type === "HEADQUARTERS")
        return filter.data.data;
    }
  }, [investmentFilters]);

  const defaultFilter = {
    countryEnum: null,
    stateEnum: null,
    city: null,
  };

  const [localFilter, setLocalFilter] = useState(
    headquartersFilter ?? defaultFilter
  );

  // Store the display text separately from the filter data
  const [selectedLocationText, setSelectedLocationText] = useState("");

  const locationSelect = (v: any, option: any) => {
    const record = option.location;
    const newFilter = {
      countryEnum: record.countryEnumId,
      stateEnum: record.stateEnumId,
      city: record.city,
    };
    const displayText = locationText(record);

    setLocalFilter(newFilter);
    setSelectedLocationText(displayText);
    setQuery(displayText);
  };

  const [query, setQuery] = useState("");

  // Set query to current filter text when filter changes
  useEffect(() => {
    if (selectedLocationText && !query) {
      setQuery(selectedLocationText);
    }
  }, [selectedLocationText, query]);

  const { data, loading } = useQuery(ADDRESS_SEARCH, {
    variables: { q: query },
    skip: query.length < 3,
  });

  const set = () => {
    if (localFilter.countryEnum || localFilter.stateEnum || localFilter.city)
      dispatch(
        updateFilter({
          type: "investment",
          data: {
            type: "firm",
            data: {
              type: "HEADQUARTERS",
              data: localFilter,
            },
          },
        })
      );
  };

  const reset = () => {
    setQuery("");
    setSelectedLocationText("");
    setLocalFilter(defaultFilter);
    dispatch(
      removeFilter({
        type: "investment",
        data: {
          type: "firm",
          data: {
            type: "HEADQUARTERS",
            data: defaultFilter,
          },
        },
      })
    );
  };

  return (
    <FilterInterface
      label={i18n.t("search_discovery.filters.labels.location")}
      count={headquartersFilter ? 1 : 0}
      reset={reset}
      set={set}
      menuClass="filters__menu-dropdown"
    >
      <div className="main-dropdown__menu-body">
        <AutoComplete
          data-cy="Headquarters-Auto-Complete"
          placeholder="Type at least 3 characters to search locations..."
          value={query}
          onSelect={locationSelect}
          onChange={setQuery}
          onClear={reset}
          options={options(data?.addressSearch).map(
            (location: types.AddressSearchItem) => ({
              label: locationText(location),
              value: locationText(location),
              location,
            })
          )}
          allowClear
          notFoundContent={
            query.length < 3
              ? "Type at least 3 characters"
              : loading
                ? "Searching..."
                : "No locations found"
          }
        />
      </div>
    </FilterInterface>
  );
}

export default HeadQuartersFilter;
